// ReadinessScoreCard — Phase 3: Production Readiness Score display
// Shows overall gauge, 4 dimension bars, status badge, and trend sparkline
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  Warning as AtRiskIcon,
  Error as NotReadyIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
} from '@mui/icons-material';
import { GaugeChart } from '../../../components/analytics/GaugeChart';
import { colors } from '../../../theme/analyticsColors';
import { getReadinessColor } from '../utils/colors';
import type { ReadinessScoreResponse, ReadinessTrendResponse } from '../../../types';

interface ReadinessScoreCardProps {
  readinessData: ReadinessScoreResponse | null | undefined;
  trendData?: ReadinessTrendResponse | null;
  loading?: boolean;
}

const statusConfig = {
  READY: { label: 'Ready', color: colors.chart.success, icon: ReadyIcon, bg: alpha(colors.chart.success, 0.08) },
  AT_RISK: { label: 'At Risk', color: colors.chart.warning, icon: AtRiskIcon, bg: alpha(colors.chart.warning, 0.08) },
  NOT_READY: { label: 'Not Ready', color: colors.chart.error, icon: NotReadyIcon, bg: alpha(colors.chart.error, 0.08) },
};

const dimensionConfig: Record<string, { label: string; color: string }> = {
  cost: { label: 'Cost', color: colors.chart.primary },
  availability: { label: 'Availability', color: colors.chart.purple },
  supplier: { label: 'Supplier', color: colors.chart.cyan },
  specification: { label: 'Specification', color: colors.chart.warning },
};

const gaugeThresholds = [
  { value: 49, color: colors.chart.error, label: 'Not Ready' },
  { value: 79, color: colors.chart.warning, label: 'At Risk' },
  { value: 100, color: colors.chart.success, label: 'Ready' },
];

const getDimensionColor = (score: number) => {
  if (score >= 80) return colors.chart.success;
  if (score >= 50) return colors.chart.warning;
  return colors.chart.error;
};

const ReadinessScoreCard: React.FC<ReadinessScoreCardProps> = ({
  readinessData,
  trendData,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Skeleton variant="circular" width={180} height={180} />
            <Box sx={{ flex: 1 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={24} sx={{ mb: 1.5, borderRadius: 1 }} />
              ))}
            </Box>
            <Skeleton variant="rectangular" width={120} height={100} sx={{ borderRadius: 1 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!readinessData) return null;

  const status = statusConfig[readinessData.status] || statusConfig.NOT_READY;
  const StatusIcon = status.icon;

  const improvement = trendData?.improvementSinceFirst ?? 0;
  const trendPoints = trendData?.trend ?? [];
  const hasTrend = trendPoints.length >= 2;

  return (
    <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.primary }}>
            Production Readiness Score
          </Typography>
          <Chip
            icon={<StatusIcon sx={{ fontSize: 16 }} />}
            label={status.label}
            size="small"
            sx={{
              bgcolor: status.bg,
              color: status.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: status.color },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {/* Left: Gauge */}
          <Box sx={{ flexShrink: 0 }}>
            <GaugeChart
              value={readinessData.overall}
              thresholds={gaugeThresholds}
              label="Overall"
              size="large"
            />
          </Box>

          {/* Center: Dimension bars */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {readinessData.dimensions.map((dim) => {
              const config = dimensionConfig[dim.dimension] || { label: dim.dimension, color: colors.secondary };
              const barColor = getDimensionColor(dim.score);
              return (
                <Box key={dim.dimension} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: colors.primary }}>
                      {config.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: barColor }}>
                        {Math.round(dim.score)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: colors.light }}>
                        / 100
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{config.label} Readiness</Typography>
                        <br />
                        <Typography variant="caption">
                          {dim.enrichedCount}/{dim.totalCount} items enriched
                        </Typography>
                        {dim.factors?.map((f, i) => (
                          <Box key={i}>
                            <Typography variant="caption">
                              {f.name}: {Math.round(f.value)} (weight: {f.weight}%)
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <LinearProgress
                      variant="determinate"
                      value={dim.score}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(barColor, 0.15),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: barColor,
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          {/* Right: Trend indicator */}
          <Box sx={{ flexShrink: 0, textAlign: 'center', minWidth: 100 }}>
            {hasTrend ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                  {improvement >= 0 ? (
                    <TrendUpIcon sx={{ fontSize: 20, color: colors.chart.success }} />
                  ) : (
                    <TrendDownIcon sx={{ fontSize: 20, color: colors.chart.error }} />
                  )}
                  <Typography sx={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: improvement >= 0 ? colors.chart.success : colors.chart.error,
                  }}>
                    {improvement >= 0 ? '+' : ''}{Math.round(improvement)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: colors.light }}>
                  since first calc
                </Typography>
                {/* Mini sparkline as simple dots */}
                <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', mt: 1 }}>
                  {trendPoints.slice(-8).map((pt, i) => {
                    const h = Math.max(4, (pt.overall / 100) * 20);
                    return (
                      <Box
                        key={i}
                        sx={{
                          width: 4,
                          height: h,
                          bgcolor: getDimensionColor(pt.overall),
                          borderRadius: 1,
                          alignSelf: 'flex-end',
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: colors.light, fontStyle: 'italic' }}>
                No trend data yet
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReadinessScoreCard;
